import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { useAuthStore, useContentStore, useSubscriptionStore } from '@shared/state';
import { uploadContent, onProcessingStatusChange, submitPriorKnowledge, triggerEmbeddings, loadUserContent } from '@shared/services/content';
import { canUpload, getSubscription, onSubscriptionChange } from '@shared/services/subscription';
import {
  getGamificationSummary,
  recordGamificationAction,
} from '@shared/services/gamification';
import type { ProcessingStatus, ContentDocument } from '@shared/types/content';
import { RidgeTexture } from '../components/ui/RidgeTexture';
import { Button } from '../components/ui/Button';
import { UploadDropzone } from '../components/upload/UploadDropzone';
import { ProcessingStatusDisplay } from '../components/upload/ProcessingStatus';
import { UploadCounter } from '../components/upload/UploadCounter';
import { PaywallModal } from '../components/subscription/PaywallModal';

type PageState = 'loading' | 'ready' | 'selected' | 'uploading' | 'processing' | 'assessing' | 'complete' | 'error' | 'blocked';

const priorKnowledgeOptions = [
  { value: 'Completely new to me', label: 'Completely new to me' },
  { value: "I've seen some of this before", label: "I've seen some of this before" },
  { value: 'I have a working understanding', label: 'I know the basics and want to go deeper' },
  { value: 'I know this area well and am refining or reviewing', label: 'I know this area well and am refining' },
];

const ERROR_MESSAGES: Record<string, string> = {
  FREE_UPLOAD_LIMIT_REACHED: "You've already used your free upload. Upgrade to keep learning with CogniPrint.",
  FREE_UPLOAD_TOO_LARGE: "This document has more than 10 pages. The free tier supports documents up to 10 pages — try uploading a single chapter or a shorter set of notes. Or upgrade to Learner for full-size uploads.",
};

export function UploadPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const store = useContentStore();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [queue, setQueue] = useState<File[]>([]);
  const [activeFile, setActiveFile] = useState<File | null>(null);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const [contentDoc, setContentDoc] = useState<ContentDocument | null>(null);
  const [submittingPK, setSubmittingPK] = useState(false);
  const [uploadsUsed, setUploadsUsed] = useState(0);
  const [existingContentId, setExistingContentId] = useState<string | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [overageNotice, setOverageNotice] = useState<string | null>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const subscription = useSubscriptionStore((s) => s.subscription);
  const setSubscription = useSubscriptionStore((s) => s.setSubscription);
  const isFree = user?.subscription === 'free';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }

    getSubscription(user.uid).then((sub) => setSubscription(sub));
    const unsubSub = onSubscriptionChange(user.uid, (sub) => setSubscription(sub));

    loadUserContent(user.uid).then((docs) => {
      const validDocs = docs.filter((d) => d.processingStatus !== 'error');
      setUploadsUsed(validDocs.length);
      if (validDocs.length > 0) {
        setExistingContentId(validDocs[0].contentId);
      }

      if (user.subscription === 'free' && validDocs.length >= 1) {
        setPageState('blocked');
      } else {
        setPageState('ready');
      }
    }).catch(() => setPageState('ready'));

    return () => unsubSub();
  }, [user, navigate, setSubscription]);

  // Listen for processing status changes on the active upload
  useEffect(() => {
    if (!store.currentContentId) return;
    let awarded = false;
    const unsub = onProcessingStatusChange(store.currentContentId, (status, doc) => {
      setProcessingStatus(status);
      setContentDoc(doc);

      if (status === 'assessing') setPageState('assessing');
      else if (status === 'complete' || status === 'embedding') setPageState('complete');
      else if (status === 'error') setPageState('error');
      else if (status !== 'uploading') setPageState('processing');

      if (!awarded && (status === 'complete' || status === 'embedding') && user && doc) {
        awarded = true;
        const subjectDomain = (doc as { subjectDomain?: string }).subjectDomain ?? null;
        const tier = user.subscription;
        const tierAllowsFreezes = tier === 'pro' || tier === 'teams';
        void (async () => {
          const summary = await getGamificationSummary(user.uid);
          const isFirst = summary.subjectsUploaded.length === 0;
          await recordGamificationAction(
            user.uid,
            isFirst ? 'first_upload' : 'content_uploaded',
            {
              counterMutations: subjectDomain ? { addSubjects: [subjectDomain] } : undefined,
              tickStreak: true,
              tierAllowsFreezes,
            },
          );
        })();
      }
    });
    return unsub;
  }, [store.currentContentId, user]);

  const handleFilesSelected = useCallback((files: File[]) => {
    setQueue((prev) => [...prev, ...files]);
    setPageState('selected');
  }, []);

  const removeFromQueue = useCallback((index: number) => {
    setQueue((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const startUpload = useCallback(async (file: File) => {
    if (!user) return;

    if (subscription) {
      const uploadCheck = canUpload(subscription);
      if (!uploadCheck.allowed) {
        setShowPaywall(true);
        return;
      }
      if (uploadCheck.isOverage && uploadCheck.overagePrice) {
        setOverageNotice(uploadCheck.reason);
      }
    }

    setActiveFile(file);
    setPageState('uploading');

    const { contentId, uploadTask } = uploadContent(user.uid, file);
    store.setCurrentContentId(contentId);

    uploadTask.on('state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        store.setUploadProgress(progress);
      },
      () => setPageState('error'),
    );

    await uploadTask;
    setPageState('processing');
  }, [user, subscription, store]);

  const handleUploadAll = useCallback(async () => {
    if (queue.length === 0) return;
    const [next, ...rest] = queue;
    setQueue(rest);
    await startUpload(next);
  }, [queue, startUpload]);

  const handlePriorKnowledge = useCallback(async (selfAssessment: string) => {
    if (!store.currentContentId) return;
    setSubmittingPK(true);
    try {
      await submitPriorKnowledge(store.currentContentId, selfAssessment);
      triggerEmbeddings(store.currentContentId).catch((err) => {
        console.warn('Embeddings failed (non-blocking):', err);
      });
      setPageState('complete');
    } catch (err) {
      console.error('Prior knowledge error:', err);
      setPageState('complete');
    } finally {
      setSubmittingPK(false);
    }
  }, [store.currentContentId]);

  const resetActive = useCallback(() => {
    setActiveFile(null);
    setProcessingStatus(null);
    setContentDoc(null);
    store.reset();
  }, [store]);

  const handleContinue = useCallback(async () => {
    setCompletedCount((n) => n + 1);
    resetActive();
    if (queue.length > 0) {
      // Process the next queued file
      const [next, ...rest] = queue;
      setQueue(rest);
      await startUpload(next);
    } else {
      setPageState('ready');
    }
  }, [queue, resetActive, startUpload]);

  const handleReset = useCallback(() => {
    setPageState('ready');
    setQueue([]);
    resetActive();
  }, [resetActive]);

  const handleRetry = useCallback(async () => {
    if (!activeFile || !user) {
      handleReset();
      return;
    }
    setPageState('uploading');
    setProcessingStatus(null);
    setContentDoc(null);
    await startUpload(activeFile);
  }, [activeFile, user, startUpload, handleReset]);

  const errorMessage = contentDoc?.processingError
    ? (ERROR_MESSAGES[contentDoc.processingError] ?? contentDoc.processingError)
    : null;

  return (
    <div className="min-h-screen bg-dark-bg relative">
      <RidgeTexture opacity="opacity-[0.03]" />

      <div className="relative z-10 max-w-[640px] mx-auto px-4 pt-32 pb-16">
        <motion.h1
          className="font-heading font-bold text-2xl sm:text-3xl mb-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Upload Content
        </motion.h1>
        <p className="text-content-muted font-body text-sm mb-8">
          {isFree
            ? 'Upload one document and CogniPrint will build a personalised learning experience.'
            : 'Upload one or more documents — CogniPrint will process them in sequence.'
          }
        </p>

        {pageState === 'loading' && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Blocked — free tier used */}
        {pageState === 'blocked' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
            <div className="rounded-lg bg-dark-surface border border-dark-border p-8">
              <p className="text-content font-body font-medium mb-3">
                You've used your free upload.
              </p>
              <p className="text-sm text-content-secondary font-body mb-6">
                Upgrade to Learner ($9.99/mo) to upload more content and unlock all 8 CogniPrint dimensions.
              </p>
              <div className="flex items-center justify-center gap-4">
                <Link to="/pricing"><Button>Upgrade</Button></Link>
                {existingContentId && (
                  <Link to={`/learn/${existingContentId}`}>
                    <Button variant="secondary">Your Learning Path</Button>
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Ready / Queue */}
        {(pageState === 'ready' || pageState === 'selected') && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <UploadDropzone onFilesSelected={handleFilesSelected} multiple={!isFree} />

            {queue.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
                <p className="text-xs text-content-muted font-body uppercase tracking-wider mb-2">
                  Queued ({queue.length})
                </p>
                <div className="space-y-2 mb-4">
                  {queue.map((file, i) => (
                    <div
                      key={`${file.name}-${i}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-dark-surface border border-dark-border"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-content font-body font-medium truncate">{file.name}</p>
                        <p className="text-xs text-content-muted font-body">
                          {(file.size / 1024 / 1024).toFixed(1)} MB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFromQueue(i)}
                        aria-label="Remove from queue"
                        className="text-content-dim hover:text-error transition-colors cursor-pointer p-1"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => setQueue([])}
                    className="text-xs text-content-muted hover:text-content font-body cursor-pointer"
                  >
                    Clear all
                  </button>
                  <Button onClick={handleUploadAll}>
                    {queue.length === 1 ? 'Upload' : `Upload ${queue.length} documents`}
                  </Button>
                </div>
              </motion.div>
            )}

            {completedCount > 0 && queue.length === 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 text-center">
                <p className="text-sm text-success font-body flex items-center justify-center gap-2">
                  <CheckCircle size={14} /> {completedCount} upload{completedCount === 1 ? '' : 's'} complete
                </p>
              </motion.div>
            )}

            <div className="mt-6">
              <UploadCounter uploadsUsed={uploadsUsed} existingContentId={existingContentId} />
            </div>
          </motion.div>
        )}

        {/* Uploading */}
        {pageState === 'uploading' && (
          <div className="py-8">
            {activeFile && (
              <p className="text-sm text-content-secondary font-body text-center mb-4 truncate">
                Uploading {activeFile.name}
              </p>
            )}
            <div className="h-2 rounded-pill bg-dark-subtle overflow-hidden mb-4">
              <motion.div
                className="h-full rounded-pill bg-gradient-to-r from-indigo-500 to-indigo-400"
                animate={{ width: `${store.uploadProgress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <p className="text-sm text-content-secondary font-body text-center">
              {Math.round(store.uploadProgress)}%
              {queue.length > 0 && <span className="text-content-dim"> · {queue.length} more queued</span>}
            </p>
          </div>
        )}

        {/* Processing */}
        {pageState === 'processing' && (
          <ProcessingStatusDisplay status={processingStatus ?? 'extracting'} document={contentDoc} />
        )}

        {/* Prior Knowledge Assessment */}
        {pageState === 'assessing' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <ProcessingStatusDisplay status="assessing" document={contentDoc} />
            <div className="mt-6">
              <p className="text-content font-body font-medium text-center mb-4">
                How would you describe your existing knowledge of this material?
              </p>
              <div className="space-y-2">
                {priorKnowledgeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handlePriorKnowledge(opt.value)}
                    disabled={submittingPK}
                    className="w-full text-left px-4 py-3 rounded bg-dark-surface border border-dark-border
                      text-sm text-content font-body hover:border-indigo-500/50 hover:bg-dark-hover
                      active:bg-indigo-500/10 transition-colors cursor-pointer min-h-[48px]
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {submittingPK && (
                <p className="mt-4 text-xs text-content-muted font-body text-center flex items-center justify-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Saving…
                </p>
              )}
            </div>
          </motion.div>
        )}

        {/* Complete */}
        {pageState === 'complete' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
            <ProcessingStatusDisplay status="complete" document={contentDoc} />
            <div className="flex flex-col items-center gap-3 mt-6">
              <Button size="lg" onClick={() => navigate(`/learn/${store.currentContentId}`)}>
                Start Learning
              </Button>
              {!isFree && (
                <button
                  onClick={handleContinue}
                  className="text-sm text-content-muted hover:text-content transition-colors cursor-pointer"
                >
                  {queue.length > 0 ? `Continue with next (${queue.length} queued)` : 'Upload another'}
                </button>
              )}
            </div>
          </motion.div>
        )}

        {/* Error */}
        {pageState === 'error' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
            {errorMessage ? (
              <div className="rounded-lg bg-dark-surface border border-dark-border p-6">
                <AlertCircle size={20} className="text-error mx-auto mb-3" />
                <p className="text-content-secondary font-body mb-6">{errorMessage}</p>
                <div className="flex items-center justify-center gap-4">
                  {contentDoc?.processingError === 'FREE_UPLOAD_LIMIT_REACHED' ? (
                    <Link to="/pricing"><Button>Upgrade</Button></Link>
                  ) : (
                    <Button onClick={handleRetry}>Try Again</Button>
                  )}
                  {contentDoc?.processingError === 'FREE_UPLOAD_TOO_LARGE' && (
                    <Link to="/pricing"><Button variant="secondary">See Plans</Button></Link>
                  )}
                  <button onClick={handleReset} className="text-xs text-content-muted hover:text-content transition-colors cursor-pointer">
                    Upload a different file
                  </button>
                </div>
              </div>
            ) : (
              <>
                <ProcessingStatusDisplay status="error" document={contentDoc} />
                <div className="flex items-center justify-center gap-4 mt-6">
                  <Button onClick={handleRetry}>Try Again</Button>
                  <button onClick={handleReset} className="text-xs text-content-muted hover:text-content transition-colors cursor-pointer">
                    Upload a different file
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}

        {overageNotice && pageState === 'selected' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-3 rounded bg-gold-500/10 border border-gold-500/30 text-sm text-gold-400 font-body text-center"
          >
            {overageNotice}
          </motion.div>
        )}
      </div>

      <PaywallModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        trigger="upload_limit"
        currentTier={subscription?.tier ?? 'free'}
      />
    </div>
  );
}
